import {
  EllipsisHorizontalIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline"
import { useMutation, useQuery } from "@tanstack/react-query"
import { SettingsSkeleton } from "components/new-stuff/ui/Skeleton"
import { Dropdown, Tag, Tooltip } from "antd"
import { Button } from "components"
import SegmentWrapper from "components/custom/SegmentWrapper"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppNotificationStore, useAuthStore } from "stores"
import { ewaAxiosInstance } from "utils/request"
import { IEWAPolicy, IGetEWAPolicies } from "../interface/policies"
import ResponsiveActinBtn from "components/new-stuff/ui/ResponsiveActinBtn"
import { IGetCompaniesRes } from "../interface/company"

const PolicyConfig = () => {
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const { user } = useAuthStore()

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [activeDeletId, setActiveDeleteId] = useState<null | IEWAPolicy>(null)

  const { isLoading: isSubmittingDelete, mutate: submitDelete } = useMutation({
    mutationFn: async () => {
      const res = await ewaAxiosInstance.delete(
        `/v1/group-policies/${activeDeletId?._id}`
      )
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        setOpenDeleteConfirmation(false)

        refetch()
      } else {
        setOpenDeleteConfirmation(false)

        toast.error(data?.message)
      }
    },
    onError: (data: any) => {
      setOpenDeleteConfirmation(false)

      toast.error(data?.msg)
    },
  })

  const {
    isLoading: isEWAPoliciesLoading,
    data: ewaPolicies,
    refetch,
  } = useQuery<IGetEWAPolicies>({
    queryKey: [`ewa-policies`],
    queryFn: async () => {
      const res = await ewaAxiosInstance.get(
        `/v1/group-policies?populate=employees`
      )
      return res.data
    },

    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const { isLoading, data: companyInfo } = useQuery<IGetCompaniesRes>({
    queryKey: [`company-info`],
    queryFn: async () => {
      const res = await ewaAxiosInstance.get(
        `/v1/employers/companies?padeCompanyId=${user?.companyIdentifier}`
      )
      return res.data
    },

    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const updatedPolicies: IEWAPolicy[] = [
    {
      _id: "general-policy",
      name: "General Policy",
      approvalRequired: companyInfo?.data?.docs?.[0]?.approvalRequired ?? false,
      withdrawalPercent: companyInfo?.data?.docs?.[0]?.withdrawalPercent ?? 0,
      company: companyInfo?.data?.docs?.[0]?._id ?? "",
      padeCompanyId: companyInfo?.data?.docs?.[0]?.padeCompanyId ?? "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      slug: "general-policy",
      __v: 0,
      constant: true,
      employees: [],
    },
    ...(ewaPolicies?.data?.docs ?? []),
  ]

  return (
    <DashboardWrapper>
      <>
        {isEWAPoliciesLoading ? (
          <SettingsSkeleton sections={3} />
        ) : (
          <SegmentWrapper
            title="EWA Policy Configuration"
            subtitle="Configure default thresholds, criteria-based rules and create new groups to control how your employees take money"
            actionBtn={
              <ResponsiveActinBtn
                items={[
                  {
                    key: "1",
                    label: (
                      <button
                        id="pf-period-new"
                        onClick={() =>
                          navigate("/dashboard/ewa/policy-management/create")
                        }
                        className="font-avenir"
                      >
                        Create EWA Policy{" "}
                      </button>
                    ),
                  },
                ]}
              >
                <Button
                  color="primary"
                  onClick={() =>
                    navigate("/dashboard/ewa/policy-management/create")
                  }
                >
                  Create EWA Policy{" "}
                </Button>
              </ResponsiveActinBtn>
            }
          >
            <div className="px-4  md:px-6 xl:px-[60px] font-circular">
              <div className="p-[13px] mb-4 bg-[#CCDBFD4D] flex items-center gap-2.5 rounded-md border border-[#CCDBFD4D]">
                <InformationCircleIcon className="h-5 w-5 text-black font-black" />
                <p className="text-neutral500 ">
                  All employees will use the default threshold until you create
                  a custom policy that will override the General policy.
                </p>
              </div>
              <div className="overflow-auto w-full ">
                {updatedPolicies?.map((item, index) => (
                  <div
                    key={index}
                    className="border border-[#E9EAEC] py-9 px-6 mb-3  rounded  items-center flex justify-between  min-w-[1024px]"
                  >
                    <div className="w-[35%]">
                      <p className="mb-2  text-padeBlack font-bold">
                        {item?.name}
                      </p>
                      <Tooltip
                        placement="top"
                        title={
                          item?.constant ? (
                            "All employees"
                          ) : item?.employees?.length === 0 ? (
                            "No employees"
                          ) : (
                            <div className="">
                              {item?.employees?.map((per, idx) => (
                                <p
                                  key={idx}
                                  className="py-2"
                                >
                                  {per?.firstName} {per?.lastName}
                                </p>
                              ))}
                            </div>
                          )
                        }
                      >
                        <p className="text-[#0C1B3DB2]">
                          {item?.constant ? "All" : item?.employees?.length}{" "}
                          Employees
                        </p>
                      </Tooltip>
                    </div>
                    <div className="w-[35%]">
                      <p className="  text-padeBlack">
                        {item?.withdrawalPercent}% of earned wages
                      </p>
                    </div>
                    <div className="w-[20%]">
                      {item?.approvalRequired ? (
                        <Tag className="bg-[#0BCE8826] text-[#0BCE88] border-none text-xs font-bold">
                          Admin Approval Required
                        </Tag>
                      ) : (
                        <Tag className="text-[#5D6B82] bg-[#E7ECF8] border-none text-xs font-bold">
                          Auto-Approval
                        </Tag>
                      )}
                    </div>
                    <div className="w-[10%] flex  justify-end">
                      {!item?.constant && (
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: "1",
                                label: (
                                  <button
                                    className="w-full flex  justify-start"
                                    onClick={() =>
                                      navigate(
                                        `/dashboard/ewa/policy-management/edit?x=${item?._id}`
                                      )
                                    }
                                  >
                                    Edit
                                  </button>
                                ),
                              },
                              {
                                key: "2",
                                label: (
                                  <button
                                    className="w-full flex  justify-start"
                                    onClick={() => {
                                      setActiveDeleteId(item)
                                      setOpenDeleteConfirmation(true)
                                    }}
                                  >
                                    Delete
                                  </button>
                                ),
                              },
                            ],
                          }}
                        >
                          <EllipsisHorizontalIcon className="h-6 w-6 text-primary500" />
                        </Dropdown>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SegmentWrapper>
        )}
        {openDeleteConfirmation && (
          <CustomModal
            open
            title="Delete this policy?"
            onCancel={() => {
              setOpenDeleteConfirmation(false)
              setActiveDeleteId(null)
            }}
            footer={
              <div className=" flex gap-4  justify-end items-center">
                <Button
                  color="secondary"
                  onClick={() => {
                    setOpenDeleteConfirmation(false)
                    setActiveDeleteId(null)
                  }}
                >
                  Cancel
                </Button>

                <Button
                  color="danger"
                  submitting={isSubmittingDelete}
                  onClick={() => {
                    submitDelete()
                  }}
                >
                  Delete Policy
                </Button>
              </div>
            }
          >
            <div className=" flex flex-col items-center">
              <p className=" text-padeBlack font-medium text-sm  ">
                This will permanently delete the {activeDeletId?.name} policy
                and affected employees will fall back to your default settings.
              </p>
            </div>
          </CustomModal>
        )}
      </>
    </DashboardWrapper>
  )
}

export default PolicyConfig
